<script setup lang="ts">
import type { MessagesComponents as components } from '@core/types'
import { useFlashProgress } from '#imports'

export interface DefaultMessageProps {
    notification: components['schemas']['RenderedNotification']
}

const props = defineProps<DefaultMessageProps>()

const emit = defineEmits<{ dismiss: [] }>()

const { progressReady, progressStyle, dismiss } = useFlashProgress(
    props.notification,
    () => emit('dismiss'),
)
</script>

<template>
    <div
        :class="['notification-flash', { 'notification-flash--has-progress': progressReady }]"
        :style="progressReady ? progressStyle : undefined"
    >
        <LazyUIAlert
            :is-reminder="true"
            closeable
            type="success"
            @closed="dismiss()"
        >
            <!-- eslint-disable vue/no-v-html — safe: title and body are HTML-sanitized by the Messages API before delivery -->
            <h4 v-if="notification.title" v-html="notification.title" />
            <div v-if="notification.body" v-html="notification.body" />
            <!-- eslint-enable vue/no-v-html -->
        </LazyUIAlert>
    </div>
</template>

<style lang="scss" scoped>
.notification-flash :deep(.alert) {
    margin-bottom: 0;
}

.notification-flash--has-progress :deep(.alert) {
    overflow: hidden;

    &::after,
    &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
    }

    &::after {
        background-color: var(--border-surface);
    }

    // Green progress — shrinks via CSS animation
    &::before {
        width: var(--progress-width, 100%);
        background-color: var(--success-light, $brand-success-border);
        animation: shrink-progress var(--progress-duration, 0s) linear forwards;
        z-index: 1;
    }
}

@keyframes shrink-progress {
    to {
        width: 0;
    }
}
</style>
